import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, API_URL } from '../utils/api';

type User = { id: string; name: string; email: string; role: string };
type Mapping = { wati_operator_email: string; crm_user_id: string; crm_user_name: string; crm_user_email: string; crm_user_role: string };
type VerificationEvent = {
  provider_message_id: string;
  event_type: string;
  event_timestamp: string | null;
  received_at: string;
  operator_email: string | null;
  operator_name: string | null;
  source_type: string | null;
  bot_indicators: string | null;
  recipient_hash: string | null;
  classification: 'human_candidate' | 'bot_candidate' | 'unknown';
  crm_user_name: string | null;
};

export default function WatiAgentVerificationPanel({ users }: { users: User[] }) {
  const [email, setEmail] = useState('');
  const [crmUserId, setCrmUserId] = useState('');
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [events, setEvents] = useState<VerificationEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingResponse, eventResponse] = await Promise.all([
        apiFetch(`${API_URL}/api/admin/wati-agent-mappings`),
        apiFetch(`${API_URL}/api/admin/wati-agent-mappings/events?limit=50`),
      ]);
      if (mappingResponse.ok) setMappings((await mappingResponse.json()).mappings || []);
      if (eventResponse.ok) setEvents((await eventResponse.json()).events || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const response = await apiFetch(`${API_URL}/api/admin/wati-agent-mappings`, {
      method: 'PUT',
      body: JSON.stringify({ wati_operator_email: email, crm_user_id: crmUserId }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      alert(body.error || 'Failed to save mapping');
      return;
    }
    setEmail('');
    setCrmUserId('');
    await load();
  };

  const remove = async (operatorEmail: string) => {
    if (!confirm('Remove this WATI operator mapping?')) return;
    await apiFetch(`${API_URL}/api/admin/wati-agent-mappings/${encodeURIComponent(operatorEmail)}`, { method: 'DELETE' });
    await load();
  };

  return (
    <section className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <h2 className="text-lg font-semibold text-slate-900">WATI Agent Mapping — Testing</h2>
      <p className="mt-1 text-xs text-slate-600">Admin-only verification. A mapping does not prove that a message was human-initiated.</p>
      <form onSubmit={save} className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="WATI operator email" className="rounded border border-slate-300 bg-white px-3 py-2 text-sm" />
        <select required value={crmUserId} onChange={(e) => setCrmUserId(e.target.value)} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">Select CRM agent</option>
          {users.map((user) => <option key={user.id} value={user.id}>{user.name} · {user.email}</option>)}
        </select>
        <button type="submit" className="rounded bg-amber-700 px-4 py-2 text-sm font-medium text-white">Save mapping</button>
      </form>
      {loading ? <p className="mt-4 text-sm text-slate-500">Loading verification data…</p> : (
        <>
          <div className="mt-5 overflow-x-auto rounded border border-amber-200 bg-white">
            <table className="w-full text-left text-xs"><thead className="bg-amber-100"><tr><th className="p-2">WATI operator</th><th className="p-2">CRM agent</th><th className="p-2" /></tr></thead>
              <tbody>{mappings.map((mapping) => <tr key={mapping.wati_operator_email} className="border-t"><td className="p-2">{mapping.wati_operator_email}</td><td className="p-2">{mapping.crm_user_name} · {mapping.crm_user_role}</td><td className="p-2 text-right"><button onClick={() => remove(mapping.wati_operator_email)} className="text-rose-700">Remove</button></td></tr>)}</tbody>
            </table>
          </div>
          <h3 className="mt-6 text-sm font-semibold text-slate-800">Recent outbound webhook metadata</h3>
          <div className="mt-2 overflow-x-auto rounded border border-amber-200 bg-white">
            <table className="w-full text-left text-xs"><thead className="bg-amber-100"><tr><th className="p-2">Received</th><th className="p-2">Event</th><th className="p-2">Operator</th><th className="p-2">Source/bot</th><th className="p-2">Classification</th><th className="p-2">Mapped agent</th></tr></thead>
              <tbody>{events.map((item) => <tr key={`${item.provider_message_id}:${item.event_type}`} className="border-t"><td className="p-2">{new Date(item.received_at).toLocaleString()}</td><td className="p-2">{item.event_type}</td><td className="p-2">{item.operator_email || 'unknown'}{item.operator_name ? ` · ${item.operator_name}` : ''}</td><td className="p-2">{item.source_type || '—'}{item.bot_indicators ? ` · ${item.bot_indicators}` : ''}</td><td className="p-2">{item.classification}</td><td className="p-2">{item.crm_user_name || '—'}</td></tr>)}</tbody>
            </table>
            {!events.length && <p className="p-3 text-xs text-slate-500">No outbound verification events recorded.</p>}
          </div>
        </>
      )}
    </section>
  );
}
